// Copyright 2024 - MinIO, Inc. All rights reserved.

import React, { Fragment } from "react";
import { ApplicationLogo, Box, Link, MinIOTierIconXs, Tooltip } from "mds";
import styled from "styled-components";
import SetupStep1 from "./SetupStep1";
import { useAppSelector } from "../../app/hooks";
import SetupStep2 from "./SetupStep2";
import SetupStep3 from "./SetupStep3";
import { Navigate } from "react-router-dom";
import SetupComplete from "./SetupComplete";

const CustomMinIOButton = styled.a(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 42,
  height: 42,
  borderRadius: 10,
  border: 0,
  borderTop: "1px solid rgba(255, 255, 255, 0.23)" as const,
  borderBottom: "1px solid rgba(255, 255, 255, 0.23)" as const,
  mixBlendMode: "overlay" as const,
  boxShadow:
    "0px 4px 9px 1px rgba(0, 0, 0, 0.19), inset 0px 2px 20px 0px rgba(255,255,255,0.6)" as const,
  backgroundColor: "rgba(190 , 121, 140, 0.20)" as const,
  color: "#fff",
  textDecoration: "none",
  "& svg": {
    height: 26,
  },
  "&:hover, &:visited, &:focus": {
    color: "#fff",
    textDecoration: "none",
  },
}));

const Setup = () => {
  const step = useAppSelector((state) => state.setup.step);
  const setupPlan = useAppSelector((state) => state.setup.setupPlan);
  const setupComplete = useAppSelector((state) => state.setup.setupComplete);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        background: "no-repeat url(/SetupBG.svg)",
        backgroundPosition: "center center",
        backgroundSize: "cover",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
        }}
      >
        <Tooltip tooltip={"Visit MinIO Website"}>
          <CustomMinIOButton href={"http://min.io/?ref=con"} target={"_blank"}>
            <MinIOTierIconXs />
          </CustomMinIOButton>
        </Tooltip>
      </Box>
      <Box
        sx={{
          maxWidth: 405,
          width: "100%",
        }}
      >
        <Box
          withBorders
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 16,
            boxSizing: "border-box",
            padding: "48px 40px 25px",
            minHeight: 550,
          }}
        >
          <Box sx={{ width: 232 }}>
            <ApplicationLogo
              applicationName={"minio"}
              subVariant={"enterpriseosvertical"}
            />
          </Box>
          {setupPlan === "register" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                gap: 5,
                padding: "15px 0px 10px",
                "& .indicator": {
                  display: "block",
                  height: 3,
                  width: 34,
                  borderRadius: 3,
                  backgroundColor: "#E5E7EB",
                  transitionDuration: "0.2s",
                  "&.active": {
                    backgroundColor: "#2B64E5",
                  },
                },
              }}
            >
              <Box className={`indicator ${step >= 1 ? "active" : ""}`} />
              <Box className={`indicator ${step >= 2 ? "active" : ""}`} />
              <Box className={`indicator ${step >= 3 ? "active" : ""}`} />
            </Box>
          )}
          {!setupComplete && (
            <Fragment>
              {step === 1 && <SetupStep1 />}
              {step === 2 && <SetupStep2 />}
              {step === 3 && <SetupStep3 />}
            </Fragment>
          )}
          {setupComplete && setupPlan === "register" && <Navigate to={"/"} />}
          {setupComplete && setupPlan === "join" && <SetupComplete />}
        </Box>
        <Box sx={{ marginTop: 27, textAlign: "center" }}>
          Unsure how to activate? Read our <Link>Documentation</Link>
        </Box>
      </Box>
    </Box>
  );
};

export default Setup;
